import { useCallback, useEffect, useRef, useState } from 'react';
import { Tooltip, notification } from 'antd';
import { Button, Search, DataGrid, Icon } from '@hydrodigital/design-system';
import { ExperimentOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router';
import ModalCreationInteractiveSimulation from './ModalCreationInteractiveSimulation';
import InteractiveSimulationService from '../../domain/InteractiveSimulations/InteractiveSimulationService';
import { setInteractiveSimulationData } from '@/redux/slices/interactiveSimulationSlice';
import { useDispatch } from 'react-redux';
import { AnyAction } from 'redux';
import ModalKeplerAnalysis from './ModalKeplerAnalysis';
import ConfirmationModal from '../Modal/ConfirmationModal';
import { InteractiveSimulation } from '@/domain/InteractiveSimulations/InteractiveSimulation';
import useDebounce from '@/hooks/useDebounce';

const interactiveSimulationService = new InteractiveSimulationService();

export default function InteractiveSimulations() {
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const [paginationState, setPaginationState] = useState({ pageIndex: 0, pageSize: 10 });
	const [totalRows, setTotalRows] = useState(0);
	const [query, setQuery] = useState<string>('');
	const [simulations, setSimulations] = useState<any[]>([]);
	const [loading, setLoading] = useState(false);

	const debouncedQuery = useDebounce(query, 500);

	const [showCreationModal, setShowCreationModal] = useState(false);
	const [showKeplerAnalysisModal, setShowKeplerAnalysismodal] = useState(false);
	const [selectedSimulation, setSelectedSimulation] = useState<any | null>(null);

	const [showConfirmDelete, setShowConfirmDelete] = useState(false);

	const [activeKeplerId, setActiveKeplerId] = useState<number | null>(null);

	const pollRef = useRef<NodeJS.Timeout | null>(null);
	const pollStartRef = useRef<number>(0);
	const prevDebouncedQueryRef = useRef(debouncedQuery);

	const handleGetSimulations = useCallback(async () => {
		try {
			setLoading(true);
			const oui = JSON.parse(localStorage.getItem('security') || '{}').organizationId;

			const res = await interactiveSimulationService.getInteractiveSimulations({
				organizationId: oui,
				name: debouncedQuery || null,
				endDate: null,
				startDate: null,
				status: null,
				page: paginationState.pageIndex + 1,
				size: paginationState.pageSize,
			});

			
			if (res && typeof res === 'object') {
				setTotalRows(res.totalRecords || 0);

				
				if (Array.isArray(res.data)) {
					setSimulations(res.data);

					if (activeKeplerId) {
						const refreshed = res.data.find((s: any) => s.id === activeKeplerId);
						if (refreshed) setSelectedSimulation(refreshed);
					}
				} else {
					
					console.error('Invalid data format from getInteractiveSimulations:', res);
					setSimulations([]);
				}
			} else {
				
				console.error('Invalid response from getInteractiveSimulations:', res);
				setSimulations([]);
			}
		} catch (error) {
			console.error('Error fetching simulations:', error);
			notification.error({
				message: 'Error',
				description: 'Failed to load simulations. Please try again later.',
			});
		} finally {
			setLoading(false);
		}
	}, [debouncedQuery, paginationState.pageIndex, paginationState.pageSize, activeKeplerId]);

	const openKeplerModal = (sim: any) => {
		console.log('sim', sim);

		setSelectedSimulation(sim);
		setActiveKeplerId(sim.id);
		setShowKeplerAnalysismodal(true);
	};

	useEffect(() => {
		
		const queryChanged = prevDebouncedQueryRef.current !== debouncedQuery;

		if (queryChanged) {
			prevDebouncedQueryRef.current = debouncedQuery;
			
			if (paginationState.pageIndex !== 0) {
				setPaginationState((p) => ({ ...p, pageIndex: 0 }));
				return; 
			}
		}

		handleGetSimulations();
	}, [paginationState.pageIndex, paginationState.pageSize, debouncedQuery, handleGetSimulations]);

	useEffect(() => {
		if (activeKeplerId) {
			pollStartRef.current = Date.now();

			pollRef.current = setInterval(async () => {
				await handleGetSimulations();
				const sim = simulations.find((s) => s.id === activeKeplerId);
				const completed =
					sim?.links?.length && sim.links.every((l: any) => l.status === 'COMPLETED');

				const elapsed = Date.now() - pollStartRef.current;
				const timeout = elapsed >= 600_000;

				if (completed || timeout) {
					clearInterval(pollRef.current!);
					pollRef.current = null;
					setActiveKeplerId(null);
					console.log(
						completed
							? '✅ Kepler COMPLETED – polling detenido'
							: '⏹ 10 min sin completarse – polling detenido',
					);
				}
			}, 20_000);
		}

		return () => {
			if (pollRef.current) {
				clearInterval(pollRef.current);
				pollRef.current = null;
			}
		};
	}, [activeKeplerId, handleGetSimulations, simulations]);
	const handleDeleteSimulations = async (id: number) => {
    try {
      const res = await interactiveSimulationService.deleteInteractiveSimulation(id);
      if (res && res.success) {
        notification.success({
          message: 'Successful',
          description: 'Interactive Simulation deleted',
        });
        setShowConfirmDelete(false);
        handleGetSimulations();
      } else {
        notification.warning({
          message: 'Warning',
          description: 'Delete failed',
        });
      }
    } catch (error) {
      notification.error({
        message: 'Error',
        description: 'Failed to delete Interactive Simulation',
      });
    }
	};

	const columns = [
		{
			accessorKey: 'name',
			header: 'Simulation Name',
			cell: (cellContext: any) => (
				<span style={{ color: '#232B36', fontSize: '14px', fontWeight: '500' }}>
					{cellContext.getValue()}
				</span>
			),
		},
		{
			accessorKey: 'inp',
			header: 'INP File',
			cell: (cellContext: any) => <span>{cellContext.getValue()}</span>,
		},
		{
			accessorKey: 'numit',
			header: 'Number of iterations',
			cell: (cellContext: any) => <span>{cellContext.getValue()}</span>,
		},
		{
			accessorKey: 'location',
			header: 'Location',
			cell: (cellContext: any) => <span>{cellContext.getValue()}</span>,
		},
		{
			accessorKey: 'createdBy',
			header: 'Created by',
			cell: (cellContext: any) => {
				const row = cellContext.row.original as InteractiveSimulation;
				return (
					<div className="flex column">
						<span style={{ color: '#232B36', fontSize: '14px', fontWeight: '500' }}>
							{row.createdBy}
						</span>
						<span style={{ color: '#74788D', fontSize: 10 }}>
							{dayjs(row.createdAt).format('YYYY/MM/DD [at] hh:mm a')}
						</span>
					</div>
				);
			},
		},
		{
			accessorKey: 'actions',
			header: 'Actions',
			cell: (cellContext: any) => {
				const rec = cellContext.row.original as InteractiveSimulation;
				return (
					<div className="flex" style={{ gap: '12px' }}>
						<Tooltip title="Replay Simulation">
							<span
								style={{ cursor: 'pointer', display: 'inline-flex' }}
								onClick={() => {
									dispatch(setInteractiveSimulationData(rec) as unknown as AnyAction);
									navigate(`/replay-interactive-simulation-v2/${rec.locationId}/${rec.id}`);
								}}
							>
								<Icon name="play-circle" size="m" hoverable color="#232B36" />
							</span>
						</Tooltip>
						<Tooltip title="Kepler analysis">
							<span
								style={{ cursor: 'pointer', display: 'inline-flex' }}
								onClick={() => openKeplerModal(rec)}
							>
								<ExperimentOutlined style={{ fontSize: 20, color: '#1a1a1a' }} />
							</span>
						</Tooltip>
						<Tooltip title="Delete">
							<span
								style={{ cursor: 'pointer', display: 'inline-flex' }}
								onClick={() => {
									setSelectedSimulation(rec);
									setShowConfirmDelete(true);
								}}
							>
								<Icon name="trash-alt" size="m" hoverable color="#232B36" />
							</span>
						</Tooltip>
					</div>
				);
			},
		},
	];

	return (
		<>
			<div className="flex justify-between mb-3">
				<div style={{ width: 200, minWidth: 200 }}>
					<Search
						placeholder="Search"
						size="small"
						value={query}
						onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
							setQuery(e.target.value);
						}}
						showClearButton={true}
						onClear={() => {
							setQuery('');
						}}
						style={{ width: '100%' }}
					/>
				</div>
				<Button
					label="Add new"
					size="small"
					variant="secondary"
					iconName="plus-circle"
					onClick={() => setShowCreationModal(true)}
				/>
			</div>

			<DataGrid
				columns={columns}
				data={simulations || []}
				loading={loading && simulations.length === 0}
				striped={true}
				hoverable={true}
				bordered={true}
				stickyHeader={true}
				maxHeight="calc(100vh - 420px)"
				pagination={{
					enabled: true,
					manualPagination: true,
					rowCount: totalRows,
					defaultPageSize: 10,
					pageSizeOptions: [10, 20, 50],
					showPageSizeSelector: true,
					showPageInfo: true,
				}}
				paginationState={paginationState}
				onPaginationChange={setPaginationState}
			/>

			{showCreationModal && (
				<ModalCreationInteractiveSimulation
					showCreationModal={showCreationModal}
					onCancel={() => setShowCreationModal(false)}
					onSuccess={handleGetSimulations}
				/>
			)}

			<ModalKeplerAnalysis
				showKeplerAnalysisModal={showKeplerAnalysisModal}
				simulation={selectedSimulation}
				onCancel={() => {
					setShowKeplerAnalysismodal(false);
					setSelectedSimulation(null);
					setActiveKeplerId(null);
				}}
				onSuccess={handleGetSimulations}
			/>

			<ConfirmationModal
				title="Delete Simulation?"
				message="Are you sure to delete it?"
				showModal={showConfirmDelete}
				onAccept={() => selectedSimulation && handleDeleteSimulations(selectedSimulation.id)}
				oncancel={() => setShowConfirmDelete(false)}
			/>
		</>
	);
}
